import React, { useEffect } from "react";
import { X } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

export default function SettingsDrawer({ open, onClose }) {
  const { mode, toggleMode, reduceMotion, setReduceMotion, fontSize, setFontSize, language, setLanguage, reset } = useTheme();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60]"
        onClick={onClose}
        aria-hidden="true"
        style={{
          background: "rgba(0,0,0,0.45)",
          animation: "mt-fade-in 300ms ease forwards",
          pointerEvents: "auto",
        }}
        data-testid="settings-backdrop"
      />
      {/* Drawer */}
      <aside
        className="top-0 right-0 h-full z-[61] w-[min(420px,92vw)] mt-glass-thick mt-rainbow-edge"
        style={{
          position: "fixed",
          borderTopLeftRadius: 22,
          borderBottomLeftRadius: 22,
          animation: "mt-settings-in 450ms cubic-bezier(0.16,1,0.3,1) forwards",
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        aria-hidden={!open}
        data-testid="settings-drawer"
      >
        <div className="flex items-center justify-between px-7 pt-7">
          <h2 className="font-display text-2xl font-light">Settings</h2>
          <button onClick={onClose} className="mt-glass rounded-full p-2" data-testid="settings-close" aria-label="Close settings">
            <X size={16} />
          </button>
        </div>
        <p className="px-7 pt-1 font-mono text-[0.65rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
          Control room · Million Threads
        </p>

        <div className="px-7 mt-8 space-y-6">
          <Row label="Dark / Light Mode">
            <button
              type="button"
              onClick={toggleMode}
              className="mt-pill"
              data-active={true}
              data-testid="settings-mode-toggle"
            >
              {mode === "dark" ? "Dark" : "Light"}
            </button>
          </Row>

          <Divider />

          <Row label="Font Size">
            <div className="flex gap-2">
              {["small", "medium", "large"].map((s) => (
                <button
                  key={s}
                  type="button"
                  className="mt-pill"
                  data-active={fontSize === s}
                  onClick={() => setFontSize(s)}
                  data-testid={`settings-font-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Row>

          <Divider />

          <Row label="Reduce Motion">
            <button
              type="button"
              className="mt-pill"
              data-active={reduceMotion}
              onClick={() => setReduceMotion(!reduceMotion)}
              data-testid="settings-reduce-motion"
            >
              {reduceMotion ? "On" : "Off"}
            </button>
          </Row>

          <Divider />

          <Row label="Language">
            <select
              className="mt-input !py-2 !px-3 !w-auto font-mono text-[0.7rem] tracking-mt uppercase"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              data-testid="settings-language"
            >
              <option value="en">English</option>
              <option value="ja">日本語</option>
              <option value="fr">Français</option>
            </select>
          </Row>
        </div>

        <div className="px-7 mt-10 pt-6 border-t" style={{ borderColor: "var(--mt-glass-border)" }}>
          <button
            type="button"
            onClick={reset}
            className="font-mono text-[0.7rem] tracking-mt uppercase opacity-60 hover:opacity-100 transition"
            data-testid="settings-reset"
          >
            Reset to Default
          </button>
        </div>
      </aside>
    </>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <span className="font-mono text-[0.7rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>{label}</span>
      <div>{children}</div>
    </div>
  );
}
function Divider() {
  return <div className="h-px w-full" style={{ background: "linear-gradient(90deg, transparent, var(--mt-glass-border), transparent)" }} />;
}
